import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'astroturf/react';
import { FeedContext } from 'js/social/contexts/feed';
import { SuggestionsContext } from 'js/social/contexts/suggestions';
import { SuggestionCard } from 'js/social/suggestions/SuggestionCard';
import { Suggestion } from 'js/social/shared/types';
import { Body } from 'js/design_system/Body';
import { ViewAllSuggestions } from 'js/social/suggestions/ViewAllSuggestions';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { getSocialImpressionSource } from '../ImpressionHelper';
import { Carousel } from 'js/social/shared/Carousel';
import { serializeUserSuggestionScoringMetadata } from 'js/social/ImpressionHelper';
import _Container from "SuggestionsCarousel-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/suggestions/SuggestionsCarousel.tsx?styleId=Container";
import _Header from "SuggestionsCarousel-Header.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/suggestions/SuggestionsCarousel.tsx?styleId=Header";
import _CarouselContainer from "SuggestionsCarousel-CarouselContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/suggestions/SuggestionsCarousel.tsx?styleId=CarouselContainer";


const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const Header = /*#__PURE__*/styled(Body, null, {
  displayName: "Header",
  styles: _Header
});

const CarouselContainer = /*#__PURE__*/styled("div", null, {
  displayName: "CarouselContainer",
  styles: _CarouselContainer
});

// A group must contain at least this many suggestions to be show in the carousel
const MIN_GROUP_SIZE = 4;

export const SuggestionsCarousel: React.FC<{ index: number }> = ({ index }) => {
  const {
    isLoading,
    getGroup,
    getRandomGroupId,
    getFirstPriorityGroupId,
    getSuggestions,
  } = useContext(SuggestionsContext);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const {
    feedConfig: { page },
  } = useContext(FeedContext);

  const group = useMemo(() => {
    return getGroup(groupId);
  }, [groupId, getGroup]);

  useEffect(() => {
    if (!isLoading && !groupId) {
      const newGroupId = getFirstPriorityGroupId(MIN_GROUP_SIZE);

      if (newGroupId) {
        setGroupId(newGroupId);
        setSuggestions(getSuggestions(newGroupId, 10));
      }
    }
  }, [
    isLoading,
    groupId,
    getRandomGroupId,
    getFirstPriorityGroupId,
    getSuggestions,
  ]);

  const cards: (Suggestion | undefined)[] = useMemo(() => {
    return [...suggestions, undefined];
  }, [suggestions]);

  if (isLoading || !group || !groupId) {
    return null;
  }

  return (
    <Container>
      <Header>{group.name}</Header>

      <CarouselContainer>
        <Carousel
          items={cards}
          fixedItemWidth={220}
          gap={16}
          keyExtractor={(item) => (item ? item.user.id : 'view-all')}
          renderItem={(item, itemIndex) =>
            item ? (
              <ImpressionWatcher
                key={item.user.id}
                source={getSocialImpressionSource(
                  page,
                  `feed-${index}`,
                  'sugg-f',
                )}
                entity_type={ImpressionEntity.User}
                entity_id={item.user.id}
                extra={{
                  category: groupId,
                  card_index: itemIndex,
                  num_cards: suggestions.length,
                  scoring: serializeUserSuggestionScoringMetadata(
                    item.scoring_metadata,
                  ),
                }}
              >
                <SuggestionCard
                  suggestion={item}
                  suggestionSource={groupId}
                  followSource="social_news_feed_carousel"
                  onIgnore={() =>
                    setSuggestions(
                      suggestions.filter((other) => item !== other),
                    )
                  }
                />
              </ImpressionWatcher>
            ) : (
              <ImpressionWatcher
                source={getSocialImpressionSource(
                  page,
                  `feed-${index}`,
                  'sugg-a',
                )}
                extra={{
                  category: groupId,
                  card_index: suggestions.length,
                }}
              >
                <ViewAllSuggestions />
              </ImpressionWatcher>
            )
          }
        />
      </CarouselContainer>
    </Container>
  );
};
